import { nodeConfig, Logger } from '@scripts/mixins';
import { getElement, getElementsArray } from '@scripts/helpers/utils';
import merge from 'lodash/merge';

const NAMESPACE = 'swap';
const DEFAULT_SELECTOR = '[data-js-swap]';

/**
 * @typedef {Object} AttributeToggle
 * @property {string} attribute Attribute name
 * @property {string} [on] Value when active
 * @property {string} [off] Value when inactive
 */

/**
 * @typedef {Object} BreakpointCompatibleSettings
 * @property {boolean} [collapse] Can the swap be deactivated
 * @property {boolean} [enabled] Enable the swap
 * @property {boolean} [groupExclusive] Only one swap in a group can be active
 * @property {boolean} [groupAlwaysOpen] At least one swap in a group must be active
 * @property {Element|string} [focusOnActivate] Element to focus on activation
 * @property {Element|string|boolean} [focusOnDeactivate] Element to focus on deactivation, true to always focus the original trigger, or false to never shift focus
 */

/**
 * @typedef {Object} AgentCompatibleSettings
 * @property {string} [principal] Principal swap's id
 */

/**
 * @typedef {Object} PrincipalSettings
 * @property {NodeList|Element[]|Element|string} target Selector for swap target elements
 * @property {string} [group] Indentifier for a group of swaps
 * @property {string} [id] Indentifier for swap, used when connecting agents to a principal
 * @property {boolean} [defaultOpen] Open the swap on init
 * @property {AttributeToggle[]} [triggerAttributes] Trigger element attributes to toggle on activate/deactivate
 * @property {AttributeToggle[]} [targetAttributes] Target element attributes to toggle on activate/deactivate
 * @property {Object<string, BreakpointCompatibleSettings>} [breakpoints] Config options for different breakpoints
 * @typedef {BreakpointCompatibleSettings & AgentCompatibleSettings & PrincipalSettings} SwapSettings
 */

export class Swap {
	/* ==========================================================================
		Config
	============================================================================= */
	/** @type {Object<string, *>} */
	static #Settings = Object.freeze({
		selector: '[data-js-swap]',
		enabledClass: 'state-swap-enabled',
		activeClass: 'state-swap-active',
	});

	/** @type {SwapSettings} */
	static #DefaultConfig = Object.freeze({
		collapse: true,
		enabled: true,
		groupExclusive: true,
		groupAlwaysOpen: false,
		defaultOpen: false,
		breakpoints: {},
	});

	/** @type {string[]} */
	static #AgentConfigOptions = Object.freeze(['principal']);

	/** @enum {string} */
	static #EnabledState = Object.freeze({
		/** Manually enabled */
		ENABLED: 'enabled',
		/** Manually disabled */
		DISABLED: 'disabled',
		/** Follows the `enabled` config value */
		CONFIG: 'config',
	});

	/** @type {string[]} */
	static #EventMethods = Object.freeze([
		'activate',
		'deactivate',
		'enable',
		'disable',
	]);

	/* ==========================================================================
		Storage
	============================================================================= */

	/** @type {Map<Element, Swap>} */
	static #instances = new Map();

	/**
	 * @param {Element} element Trigger element
	 * @returns {Swap|undefined} Instance
	 */
	static #getInstance(element) {
		const instance = this.#instances.get(element);
		if (!instance) {
			Logger.warn(`Swap: No instance found for`, element);
		}
		return instance;
	}

	/**
	 * @returns {IterableIterator<[Element, Swap]>}
	 */
	static #getAllInstances() {
		return this.#instances.entries();
	}

	/** @type {Object<string, Map<Element, Set<Function>>>} */
	static #eventHandlers = {};
	static {
		this.#EventMethods.forEach((event) => {
			this.#eventHandlers[event] = new Map();
		});
	}

	static #pushEventHandler(element, event, handler) {
		if (!this.#eventHandlers[event].has(element)) {
			this.#eventHandlers[event].set(element, new Set());
		}
		this.#eventHandlers[event].get(element).add(handler);
	}

	static #removeEventHandler(element, event, handler) {
		if (!this.#eventHandlers[event].has(element)) return;

		this.#eventHandlers[event].get(element).delete(handler);
	}

	/* ==========================================================================
		Public Methods
	============================================================================= */

	static activate(element) {
		const instance = this.#getInstance(getElement(element));
		if (!instance) return;

		instance.#activate();
	}

	static deactivate(element) {
		const instance = this.#getInstance(getElement(element));
		if (!instance) return;

		instance.#deactivate();
	}

	static enable(element) {
		const instance = this.#getInstance(getElement(element));
		if (!instance) return;

		instance.#enable();
	}

	static disable(element) {
		const instance = this.#getInstance(getElement(element));
		if (!instance) return;

		instance.#disable();
	}

	static destroy(element) {
		const instance = this.#getInstance(getElement(element));
		if (!instance) return;

		instance._destroy();
	}

	static on(element, event, handler) {
		if (!this.#EventMethods.includes(event)) {
			Logger.warn(`Swap: Unsupported event '${event}'`);
			return;
		}

		this.#pushEventHandler(getElement(element), event, handler);
	}

	static off(element, event, handler) {
		if (!this.#EventMethods.includes(event)) {
			Logger.warn(`Swap: Unsupported event '${event}'`);
			return;
		}

		this.#removeEventHandler(getElement(element), event, handler);
	}

	/* ==========================================================================
		Instance Functionality
	============================================================================= */

	/** @type {Element} */
	elNode;
	/** @type {SwapSettings} */
	#config;
	/** @type {SwapSettings} */
	config;
	/** @type {string} */
	_enabled;
	/** @type {boolean} */
	_active;
	/** @type {number} */
	_activatedAt;
	/** @type {?Swap} */
	_activatedBy;

	/**
	 * @param {Element} elNode Trigger element
	 * @param {SwapSettings} config Instance settings
	 */
	constructor(elNode, config) {
		Swap.#instances.set(elNode, this);

		this.elNode = elNode;
		this.#config = merge({}, Swap.#DefaultConfig, config);

		this.config = new Proxy(this.#config, {
			get: (obj, prop) => {
				if (obj.principal && Swap.#AgentConfigOptions.includes(prop)) {
					return obj[prop];
				} else if (obj.principal) {
					return undefined;
				}

				if (!obj?.breakpoints) {
					return obj[prop];
				}

				const matchingBreakpoint = Object.entries(obj.breakpoints).find(
					([mq, config]) => matchMedia(mq).matches
				);
				if (!matchingBreakpoint) {
					return obj[prop];
				}

				return matchingBreakpoint[1][prop] ?? obj[prop];
			}
		});

		this._enabled = Swap.#EnabledState.CONFIG;
		this._active = false;
		this._activatedAt = 0;
		this._activatedBy = null;
		this._eventListeners = new Map();

		this.init();
	}


	/** @returns {boolean} */
	get isPrincipal() {
		return this.#config?.principal == null;
	}

	/** @returns {boolean} */
	get isActive() {
		return this._enabled && this._active;
	}

	/** @returns {boolean} */
	get isEnabled() {
		if (this._enabled === Swap.#EnabledState.CONFIG) {
			return this.config.enabled;
		}

		return this._enabled === Swap.#EnabledState.ENABLED;
	}

	/** @returns {Swap[]} */
	get groupInstances() {
		const principal = this.principalInstance;
		if (!principal?.config.group) return [];
		return [...Swap.#getAllInstances()]
			.filter(([node, instance]) => {
				return (
					instance.config.group &&
					instance.config.group === principal.config.group &&
					instance.isPrincipal &&
					instance.isEnabled
				);
			})
			.map(([node, instance]) => instance);
	}

	/**
	 * Group instances sorted in DOM order
	 *
	 * @returns {Swap[]}
	 */
	get sortedGroupInstances() {
		return [...this.groupInstances].sort((a, b) => {
			if (a.elNode === b.elNode) return 0;
			return a.elNode.compareDocumentPosition(b.elNode) & 2 ? 1 : -1;
		});
	}

	/** @returns {number} */
	get activatedAt() {
		return this._activatedAt;
	}

	get activatedBy() {
		return this._activatedBy;
	}

	/** @param {Swap} swapInstance Swap that triggered activation */
	set activatedBy(swapInstance) {
		this._activatedBy = swapInstance;
	}

	/** @returns {?Swap} */
	get principalInstance() {
		if (this.isPrincipal) return this;

		const filteredInstances = [...Swap.#getAllInstances()]
			.filter(
				([node, instance]) =>
					instance.config.id && instance.config.id === this.config.principal
			)
			.map(([node, instance]) => instance);

		return filteredInstances[0] || null;
	}

	/** @returns {Swap[]} */
	get linkedInstances() {
		const principal = this.principalInstance;
		if (!principal) return [this];

		const instances = [...Swap.#getAllInstances()]
			.filter(
				([node, instance]) =>
					instance.config.principal && instance.config.principal === principal.config.id
			)
			.map(([node, instance]) => instance);

		instances.push(principal);

		return instances;
	}

	/** @returns {Element[]} */
	get targetNodes() {
		if (!this.config.target) return [];
		return getElementsArray(this.config.target);
	}

		/**
	 * @param {Element} elNode Target for attribute toggle
	 * @param {AttributeToggle} attr Attribute config
	 */
	_toggleActiveAttribute(elNode, attr) {
		if ((!attr.on && this.isActive) || (!attr.off && !this.isActive)) {
			elNode.removeAttribute(attr.attribute);
			return;
		}

		elNode.setAttribute(attr.attribute, this.isActive ? attr.on : attr.off);
	}

	_activateFocus() {
		if (!this.config.focusOnActivate) return;

		const focusNode = getElement(this.config.focusOnActivate);
		focusNode?.focus();
	}

	/** @param {Swap} swapInstance Swap that triggered activation */
	_deactivateFocus(swapInstance) {
		if (this.config.focusOnDeactivate === false) return;
		if (!this.config.focusOnActivate && this.config.focusOnDeactivate == null) return;

		if (
			(this.config.focusOnActivate && this.config.focusOnDeactivate == null) ||
			this.config.focusOnDeactivate === true
		) {
			swapInstance.elNode.focus();
			return;
		}

		const focusNode = getElement(this.config.focusOnDeactivate);
		focusNode?.focus();
	}

	_runActivateSideEffects() {
		// Update triggers
		const linkedInstances = this.linkedInstances;
		linkedInstances.forEach((instance) => {
			instance.elNode.classList.toggle(Swap.#Settings.activeClass, this.isActive);
			instance.#runEventHandlers(this.isActive ? 'activate' : 'deactivate');

			this.config.triggerAttributes?.filter((attr) => !!attr.attribute)
				.forEach((attr) => {
					this._toggleActiveAttribute(instance.elNode, attr);
				});
		});

		// Update targets
		const targets = this.targetNodes;
		targets.forEach((target) => {
			target.classList.toggle(Swap.#Settings.activeClass, this.isActive);

			this.config.targetAttributes?.filter((attr) => !!attr.attribute)
				.forEach((attr) => {
					this._toggleActiveAttribute(target, attr);
				});
		});
	}

	_runEnableSideEffects() {
		// Update triggers
		const linkedInstances = this.linkedInstances;
		linkedInstances.forEach((instance) => {
			instance.elNode.classList.toggle(Swap.#Settings.enabledClass, this.isEnabled);
			instance.#runEventHandlers(this.isEnabled ? 'enable' : 'disable');
		});

		// Update targets
		const targets = this.targetNodes;
		targets.forEach((target) => {
			target.classList.toggle(Swap.#Settings.enabledClass, this.isEnabled);
		});
	}

	/** @param {Swap} triggerInstance Swap that is activating this instance */
	_activateSwap(triggerInstance = this, skipFocus = false) {
		if (this.config.group && this.config.groupExclusive) {
			const groupInstances = this.groupInstances.filter((instance) => instance !== this);
			groupInstances
				.filter((instance) => instance.isActive)
				.forEach((instance) => {
					instance._deactivateSwap(true, true);
				});
		}

		this._active = true;
		this._activatedAt = Date.now();
		this.activatedBy = triggerInstance;

		this._runActivateSideEffects();
		if (!skipFocus) this._activateFocus();
	}

	_deactivateSwap(skipGroupCheck = false, skipFocus = false) {
		if (this.config.group && this.config.groupAlwaysOpen && !skipGroupCheck) {
			const openInGroup = this.groupInstances.filter((instance) => instance !== this && instance.isActive);
			if (!openInGroup.length) return;
		}

		this._active = false;
		this._activatedAt = 0;

		this._runActivateSideEffects();
		if (!skipFocus) this._deactivateFocus(this.activatedBy);

		this.activatedBy = null;
	}

	#runEventHandlers(event) {
		Swap.#eventHandlers[event].get(this.elNode)?.forEach((handler) => {
			handler(this.principalInstance);
		});
	}

	#activate() {
		const principal = this.principalInstance;
		if (!principal) return;

		this.principalInstance._activateSwap(this);
	}

	#deactivate() {
		const principal = this.principalInstance;
		if (!principal) return;

		if (!this.principalInstance.config.collapse) return;

		this.principalInstance._deactivateSwap();
	}

	#enable() {
		const principal = this.principalInstance;
		if (!principal) return;

		this.principalInstance._enabled = Swap.#EnabledState.ENABLED;
		this.principalInstance._runEnableSideEffects();
	}

	#disable() {
		const principal = this.principalInstance;
		if (!principal) return;

		this.principalInstance._enabled = Swap.#EnabledState.DISABLED;
		this.principalInstance._runEnableSideEffects();
	}

	_destroy() {
		if (this.isPrincipal) {
			this._deactivateSwap(true, true);
			this.#disable();

			this.linkedInstances.filter((instance) => instance !== this)
				.forEach((instance) => {
					instance._destroy();
				});
		} else {
			this.elNode.classList.remove(Swap.#Settings.activeClass);
			this.elNode.classList.remove(Swap.#Settings.enabledClass);
		}

		[...this._eventListeners.entries()].forEach(([target, listeners]) => {
			Object.entries(listeners).forEach(([event, handler]) => {
				target.removeEventListener(event, handler);
			});
		});

		Swap.#instances.delete(this.elNode);
	}

	_handleConfigBreakpoint() {
		const allGroupInstances = this.sortedGroupInstances;
		const activeGroupInstances = allGroupInstances
			.filter((instance) => instance.isActive)
			.sort((a, b) => b.activatedAt - a.activatedAt);

		// If only one swap in the group can be active and more than one are, deactivate all but the most recently activated
		if (this.config.groupExclusive && activeGroupInstances.length > 1) {
			activeGroupInstances
				.slice(1)
				.forEach((instance) => {
					instance._deactivateSwap(true, true);
				});
		}

		// If one swap in the group must be active and none are, activate the first in the DOM
		if (this.config.groupAlwaysOpen && activeGroupInstances.length === 0) {
			allGroupInstances[0]._activateSwap(this, true);
		}

		// Force swap to be active if not collapsible
		if (!this.config.collapse && !this.isActive) {
			this._activateSwap(this, true);
		}

		// If enabled state is still tied to the config value, run enable side effects
		if (this._enabled === Swap.#EnabledState.CONFIG) {
			this._runEnableSideEffects();
		}
	}

	_handleClick() {
		if (!this.principalInstance?.isEnabled) return;

		if (this.principalInstance?.isActive) {
			this.#deactivate();
		} else {
			this.#activate();
		}
	}

	bindUI() {
		const boundClickEvent = this._handleClick.bind(this);
		this._eventListeners.set(this.elNode, {
			click: boundClickEvent
		});
		this.elNode.addEventListener('click', boundClickEvent);

		Object.keys(this.config.breakpoints || {}).forEach((mq) => {
			const mm = window.matchMedia(mq);
			const boundBreakpointEvent = this._handleConfigBreakpoint.bind(this);
			this._eventListeners.set(mm, {
				change: boundBreakpointEvent
			});
			mm.addEventListener('change', boundBreakpointEvent);
		});
	}

	init() {
		this.bindUI();

		this.principalInstance?._runActivateSideEffects();

		if (this.principalInstance?._enabled === Swap.#EnabledState.CONFIG) {
			this.principalInstance?._runEnableSideEffects();
		}

		if (this.principalInstance?.config.defaultOpen) {
			this.principalInstance?._activateSwap(this, true);
		}

		if (this.principalInstance?.config.group && this.principalInstance?.config.groupAlwaysOpen) {
			const allGroupInstances = this.sortedGroupInstances;
			const activeGroupInstances = allGroupInstances
				.filter((instance) => instance.isActive)
				.sort((a, b) => b.activatedAt - a.activatedAt);

			if (activeGroupInstances.length === 0) {
				allGroupInstances[0]._activateSwap(this, true);
			}
		}
	}
}

export default function factory(selector = DEFAULT_SELECTOR) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	const instances = Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE);
		const module = new Swap(node, config);

		return module;
	});

	return instances;
}
